/**
 * @fileoverview AdminSchool Component - School administration and quiz management interface
 * 
 * This component provides a comprehensive dashboard for school administrators to:
 * - Manage school information (name, settings)
 * - Create and manage quizzes with multiple-choice questions
 * - Configure point values for quiz questions
 * - Navigate to other administrative functions (leaderboard)
 * 
 * Features:
 * - Real-time school data persistence to backend database
 * - Quiz builder with multiple-choice question creation
 * - Dynamic quiz and question management (add/delete)
 * - Visual indicators for correct answers in quiz previews
 * - Secure logout functionality with confirmation dialogs
 * - Responsive interface with proper form validation
 * 
 * Data Flow:
 * 1. Loads school and quiz data from API endpoints on component mount
 * 2. Provides form interfaces for creating/editing school and quiz content
 * 3. Automatically saves changes to backend via REST API calls
 * 4. Maintains local state synchronization with server data
 * 
 * @author Harry Potter Quiz App
 * @version 1.0.0
 */

import { useEffect, useState } from 'react';
import { alertSuccess, alertWarning } from '../utils/alerts';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser } from '../App.jsx';
import Swal from 'sweetalert2';

/**
 * AdminSchool Component - Administrative dashboard for school and quiz management
 * 
 * Provides a complete interface for school administrators to manage their educational
 * environment, including school settings and quiz content creation.
 * 
 * @component
 * @returns {JSX.Element} The administrative dashboard interface
 */
export default function AdminSchool() {
  /** @type {[Object, Function]} schoolData - Current school information and settings */
  const [schoolData, setSchoolData] = useState({ id: '', name: '' });
  
  /** @type {[Array, Function]} quizCollection - Collection of all quizzes for this school */
  const [quizCollection, setQuizCollection] = useState([]);
  
  /** @type {[Array, Function]} activityCollection - Collection of all activities for this school */
  const [activityCollection, setActivityCollection] = useState([]);
  
  /** @type {[Array, Function]} wordScrambleCollection - Collection of all word scrambles for this school */
  const [wordScrambleCollection, setWordScrambleCollection] = useState([]);
  
  /** @type {[boolean, Function]} isEditingSchoolName - Whether school name is currently being edited */
  const [isEditingSchoolName, setIsEditingSchoolName] = useState(false);
  
  /** @type {[string, Function]} newQuizTitle - Title for quiz currently being created */
  const [newQuizTitle, setNewQuizTitle] = useState('');
  
  /** @type {[string|null, Function]} activeQuizId - ID of currently selected quiz for question editing */
  const [activeQuizId, setActiveQuizId] = useState(null);
  
  /** @type {[string, Function]} questionText - Text content for new question being created */
  const [questionText, setQuestionText] = useState('');
  
  /** @type {[Array, Function]} answerOptions - Four answer choices for new question */
  const [answerOptions, setAnswerOptions] = useState(['', '', '', '']);
  
  /** @type {[number, Function]} correctAnswerIndex - Index (0-3) of the correct answer option */
  const [correctAnswerIndex, setCorrectAnswerIndex] = useState(0);
  
  /** @type {[number, Function]} questionPointValue - Point value awarded for correct answer */
  const [questionPointValue, setQuestionPointValue] = useState(1);
  
  /** @type {[string, Function]} newActivityName - Name for activity currently being created */
  const [newActivityName, setNewActivityName] = useState('');
  
  /** @type {[number, Function]} newActivityPoints - Point value for activity currently being created */
  const [newActivityPoints, setNewActivityPoints] = useState(10);
  
  /** @type {[string, Function]} newScrambledWord - Scrambled word for word scramble currently being created */
  const [newScrambledWord, setNewScrambledWord] = useState('');
  
  /** @type {[string, Function]} newUnscrambledWord - Unscrambled word for word scramble currently being created */
  const [newUnscrambledWord, setNewUnscrambledWord] = useState('');
  
  /** @type {[number, Function]} newScramblePoints - Point value for word scramble currently being created */
  const [newScramblePoints, setNewScramblePoints] = useState(10);
  
  /** @type {Object|null} currentUser - Currently authenticated admin user */
  const currentUser = getCurrentUser();
  
  /** @type {Function} navigate - React Router navigation function */
  const navigate = useNavigate();

  /**
   * Initialize component data by loading school and quiz information from backend
   * 
   * Fetches current school settings and quiz collection, then sanitizes the data
   * to ensure proper structure. Automatically enters edit mode for school name
   * if no name is currently set.
   */
  useEffect(() => {
    async function initializeComponentData() {
      if (!currentUser) return;
      
      try {
        // Load school information from backend
        const schoolResponse = await fetch(`/api/school/${currentUser.code}`);
        const schoolInfo = await schoolResponse.json();
        setSchoolData(schoolInfo);
        
        // Enter edit mode if school name hasn't been set yet
        setIsEditingSchoolName(!(schoolInfo && schoolInfo.name));

        // Load quiz collection and ensure proper data structure
        const quizResponse = await fetch(`/api/quizzes/${currentUser.code}`);
        const quizData = await quizResponse.json();
        const sanitizedQuizzes = quizData.map(quiz => ({
          ...quiz,
          questions: Array.isArray(quiz.questions) ? quiz.questions : []
        }));
        setQuizCollection(sanitizedQuizzes);

        // Load activities
        const activitiesResponse = await fetch(`/api/activities/${currentUser.code}`);
        const activitiesData = await activitiesResponse.json();
        setActivityCollection(Array.isArray(activitiesData) ? activitiesData : []);

        // Load word scrambles
        const scramblesResponse = await fetch(`/api/word-scrambles/${currentUser.code}`);
        const scramblesData = await scramblesResponse.json();
        setWordScrambleCollection(Array.isArray(scramblesData) ? scramblesData : []);
      } catch (error) {
        console.error('Failed to load school data:', error);
        await alertWarning('Load Error', 'Failed to load school data. Please refresh the page.');
      }
    }
    
    initializeComponentData();
  }, [currentUser?.code]);

  /**
   * Persists school information to the backend database
   * 
   * Saves the current school data including name and creator information.
   * Exits edit mode if a valid school name was provided.
   * 
   * @async
   * @function saveSchoolInformation
   * @returns {Promise<void>}
   */
  const saveSchoolInformation = async () => {
    try {
      const payload = { ...schoolData, creatorId: currentUser.id, name: (schoolData.name || '').slice(0, 50) };
      await fetch(`/api/school/${currentUser.code}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      
      await alertSuccess('Saved', 'School information saved successfully.');
      
      // Exit edit mode if a valid name was provided
      if (schoolData.name && schoolData.name.trim()) {
        setIsEditingSchoolName(false);
      }
    } catch (error) {
      console.error('Failed to save school:', error);
      await alertWarning('Save Error', 'Failed to save school information. Please try again.');
    }
  };

  /**
   * Persists quiz collection to backend database
   * 
   * Updates both local state and backend storage with the provided quiz data.
   * Maintains synchronization between frontend and backend quiz collections.
   * 
   * @async
   * @function persistQuizCollection
   * @param {Array} updatedQuizzes - The updated quiz collection to save
   * @returns {Promise<void>}
   */
  const persistQuizCollection = async (updatedQuizzes) => {
    try {
      setQuizCollection(updatedQuizzes);
      await fetch(`/api/quizzes/${currentUser.code}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quizzes: updatedQuizzes }),
      });
    } catch (error) {
      console.error('Failed to save quizzes:', error);
      await alertWarning('Save Error', 'Failed to save quiz changes. Please try again.');
    }
  };

  /**
   * Creates a new quiz with the provided title
   * 
   * Validates input, generates a unique ID, and adds the quiz to the collection.
   * Clears the input field after successful creation.
   * 
   * @function createNewQuiz
   * @returns {Promise<void>}
   */
  const createNewQuiz = async () => {
    if (!newQuizTitle.trim()) {
      return alertWarning('Missing title', 'Please enter a quiz title.');
    }
    
    const quizId = 'q_' + Math.random().toString(36).slice(2, 8);
    const updatedQuizzes = [...quizCollection, { 
      id: quizId, 
      title: newQuizTitle.trim(), 
      questions: [] 
    }];
    
    await persistQuizCollection(updatedQuizzes);
    setNewQuizTitle('');
  };

  /**
   * Removes a quiz from the collection
   * 
   * Filters out the specified quiz and updates the collection.
   * Clears the active selection if the deleted quiz was currently selected.
   * 
   * @function removeQuiz
   * @param {string} quizId - ID of the quiz to delete
   * @returns {Promise<void>}
   */
  const removeQuiz = async (quizId) => {
    const result = await Swal.fire({
      title: 'Delete quiz?',
      text: 'This will permanently delete the quiz and all its questions. This cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel',
      background: '#0f172a',
      color: '#e2e8f0',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280'
    });

    if (!result.isConfirmed) return;

    const updatedQuizzes = quizCollection.filter(quiz => quiz.id !== quizId);
    await persistQuizCollection(updatedQuizzes);

    // Clear selection if the deleted quiz was currently active
    if (activeQuizId === quizId) {
      setActiveQuizId(null);
    }
  };

  /**
   * Adds a new question to the currently selected quiz
   * 
   * Validates that a quiz is selected and all required fields are filled.
   * Creates a new question object with the provided text, options, correct answer,
   * and point value, then adds it to the selected quiz.
   * 
   * @function addQuestionToActiveQuiz
   * @returns {Promise<void>}
   */
  const addQuestionToActiveQuiz = async () => {
    // Validate quiz selection
    if (!activeQuizId) {
      return alertWarning('Select quiz', 'Please select a quiz first.');
    }
    
    // Validate required fields
    if (!questionText.trim() || answerOptions.some(option => !option.trim())) {
      return alertWarning('Missing fields', 'Please fill in all question fields.');
    }
    
    // Create updated quiz collection with new question
    const updatedQuizzes = quizCollection.map(quiz => {
      if (quiz.id === activeQuizId) {
        const newQuestion = {
          text: questionText.trim(),
          options: answerOptions.map(opt => opt.trim()),
          correctIndex: correctAnswerIndex,
          points: Number(questionPointValue) || 0
        };
        return { 
          ...quiz, 
          questions: [...quiz.questions, newQuestion] 
        };
      }
      return quiz;
    });
    
    await persistQuizCollection(updatedQuizzes);
    
    // Reset form fields
    setQuestionText('');
    setAnswerOptions(['', '', '', '']);
    setCorrectAnswerIndex(0);
    setQuestionPointValue(1);
  };

  /**
   * Removes a specific question from a quiz
   * 
   * Locates the specified quiz and removes the question at the given index.
   * Updates the quiz collection to reflect the change.
   * 
   * @function removeQuestionFromQuiz
   * @param {string} quizId - ID of the quiz containing the question
   * @param {number} questionIndex - Index of the question to remove
   * @returns {Promise<void>}
   */
  const removeQuestionFromQuiz = async (quizId, questionIndex) => {
    const result = await Swal.fire({
      title: 'Delete question?',
      text: 'This will remove the selected question from the quiz. This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel',
      background: '#0f172a',
      color: '#e2e8f0',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280'
    });

    if (!result.isConfirmed) return;

    const updatedQuizzes = quizCollection.map(quiz => {
      if (quiz.id === quizId) {
        const filteredQuestions = quiz.questions.filter((_, index) => index !== questionIndex);
        return { ...quiz, questions: filteredQuestions };
      }
      return quiz;
    });

    await persistQuizCollection(updatedQuizzes);
  };

  /**
   * Persists activity collection to backend database
   * 
   * @async
   * @function persistActivityCollection
   * @param {Array} updatedActivities - The updated activity collection to save
   * @returns {Promise<void>}
   */
  const persistActivityCollection = async (updatedActivities) => {
    try {
      setActivityCollection(updatedActivities);
      await fetch(`/api/activities/${currentUser.code}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ activities: updatedActivities }),
      });
    } catch (error) {
      console.error('Failed to save activities:', error);
      await alertWarning('Save Error', 'Failed to save activity changes. Please try again.');
    }
  };

  /**
   * Creates a new activity with the provided name and points
   * 
   * @function createNewActivity
   * @returns {Promise<void>}
   */
  const createNewActivity = async () => {
    if (!newActivityName.trim()) {
      return alertWarning('Missing name', 'Please enter an activity name.');
    }
    
    if (typeof newActivityPoints !== 'number' || newActivityPoints < 0) {
      return alertWarning('Invalid points', 'Please enter a valid point value.');
    }
    
    const activityId = 'act_' + Math.random().toString(36).slice(2, 10);
    const updatedActivities = [...activityCollection, { 
      id: activityId, 
      name: newActivityName.trim(), 
      points: Number(newActivityPoints)
    }];
    
    await persistActivityCollection(updatedActivities);
    setNewActivityName('');
    setNewActivityPoints(10);
    await alertSuccess('Activity added', 'The activity has been added successfully.');
  };

  /**
   * Removes an activity from the collection
   * 
   * @function removeActivity
   * @param {string} activityId - ID of the activity to delete
   * @returns {Promise<void>}
   */
  const removeActivity = async (activityId) => {
    const result = await Swal.fire({
      title: 'Delete activity?',
      text: 'This will permanently delete the activity. This cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel',
      background: '#0f172a',
      color: '#e2e8f0',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280'
    });

    if (!result.isConfirmed) return;

    try {
      await fetch(`/api/activities/${currentUser.code}/${activityId}`, {
        method: 'DELETE',
      });
      
      const updatedActivities = activityCollection.filter(activity => activity.id !== activityId);
      setActivityCollection(updatedActivities);
      await alertSuccess('Deleted', 'Activity has been deleted.');
    } catch (error) {
      console.error('Failed to delete activity:', error);
      await alertWarning('Delete Error', 'Failed to delete activity. Please try again.');
    }
  };

  /**
   * Persists word scramble collection to backend database
   * 
   * @async
   * @function persistWordScrambleCollection
   * @param {Array} updatedScrambles - The updated word scramble collection to save
   * @returns {Promise<void>}
   */
  const persistWordScrambleCollection = async (updatedScrambles) => {
    try {
      setWordScrambleCollection(updatedScrambles);
      await fetch(`/api/word-scrambles/${currentUser.code}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scrambles: updatedScrambles }),
      });
    } catch (error) {
      console.error('Failed to save word scrambles:', error);
      await alertWarning('Save Error', 'Failed to save word scramble changes. Please try again.');
    }
  };

  /**
   * Creates a new word scramble with the provided words and points
   * 
   * @function createNewWordScramble
   * @returns {Promise<void>}
   */
  const createNewWordScramble = async () => {
    if (!newScrambledWord.trim() || !newUnscrambledWord.trim()) {
      return alertWarning('Missing words', 'Please enter both scrambled and unscrambled words.');
    }
    
    if (typeof newScramblePoints !== 'number' || newScramblePoints < 0) {
      return alertWarning('Invalid points', 'Please enter a valid point value.');
    }
    
    const scrambleId = 'scr_' + Math.random().toString(36).slice(2, 10);
    const updatedScrambles = [...wordScrambleCollection, { 
      id: scrambleId, 
      scrambledWord: newScrambledWord.trim(), 
      unscrambledWord: newUnscrambledWord.trim(),
      points: Number(newScramblePoints)
    }];
    
    await persistWordScrambleCollection(updatedScrambles);
    setNewScrambledWord('');
    setNewUnscrambledWord('');
    setNewScramblePoints(10);
    await alertSuccess('Word scramble added', 'The word scramble has been added successfully.');
  };

  /**
   * Removes a word scramble from the collection
   * 
   * @function removeWordScramble
   * @param {string} scrambleId - ID of the word scramble to delete
   * @returns {Promise<void>}
   */
  const removeWordScramble = async (scrambleId) => {
    const result = await Swal.fire({
      title: 'Delete word scramble?',
      text: 'This will permanently delete the word scramble. This cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel',
      background: '#0f172a',
      color: '#e2e8f0',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280'
    });

    if (!result.isConfirmed) return;

    try {
      await fetch(`/api/word-scrambles/${currentUser.code}/${scrambleId}`, {
        method: 'DELETE',
      });
      
      const updatedScrambles = wordScrambleCollection.filter(scramble => scramble.id !== scrambleId);
      setWordScrambleCollection(updatedScrambles);
      await alertSuccess('Deleted', 'Word scramble has been deleted.');
    } catch (error) {
      console.error('Failed to delete word scramble:', error);
      await alertWarning('Delete Error', 'Failed to delete word scramble. Please try again.');
    }
  };

  /**
   * Handle logout with confirmation
   */
  const handleSecureLogout = async () => {
    const result = await Swal.fire({
      title: 'Logout?',
      text: 'Are you sure you want to logout?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, logout',
      cancelButtonText: 'Cancel',
      background: '#0f172a',
      color: '#e2e8f0',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280'
    });
    
    if (result.isConfirmed) {
      // Clear all user session data
      if (window.localStorage) window.localStorage.removeItem('hp_user');
      if (window.sessionStorage) window.sessionStorage.removeItem('hp_user');
      window.location.href = '/';
    }
  };

  return (
    <div style={styles.mainContainer}>
      {/* Admin header with user info and navigation */}
      <header style={styles.adminHeader}>
        <div>Logged in as: <strong>{currentUser?.name || currentUser?.id}</strong></div>
        <div>Game Code: <strong>{currentUser?.code}</strong></div>
        <div style={styles.navigationButtons}>
          <button onClick={() => navigate('/admin/leaderboard')} style={styles.secondaryButton}>
            Leaderboard
          </button>
          <button onClick={handleSecureLogout} style={styles.secondaryButton}>
            Logout
          </button>
        </div>
      </header>
      {/* School name management section */}
      <section style={styles.schoolNameSection}>
        <h2 style={styles.sectionTitle}>School Name</h2>
        {(!isEditingSchoolName && schoolData.name) ? (
          <div style={styles.schoolNameDisplay}>
            <div style={styles.schoolNameText}>
              {schoolData.name}
            </div>
            <button onClick={() => setIsEditingSchoolName(true)} style={styles.secondaryButton}>
              Edit
            </button>
          </div>
        ) : (
          <div style={styles.schoolNameEditor}>
            <label>
              <div style={styles.fieldLabel}>School name</div>
              <input
                value={schoolData.name || ''}
                onChange={(e) => setSchoolData({ ...schoolData, name: (e.target.value || '').slice(0, 50) })}
                style={styles.inputField}
                placeholder="Enter your school name"
                aria-label="School name"
                maxLength={50}
              />
            </label>
            <div style={{ fontSize: 12, color: '#94a3b8' }}>{(50 - ((schoolData.name || '').length))} characters remaining</div>
            <div style={styles.buttonGroup}>
              <button onClick={async () => { setSchoolData({ ...schoolData, name: (schoolData.name || '').slice(0,50) }); await saveSchoolInformation(); }} style={styles.primaryButton}>
                Save school
              </button>
              {!schoolData.name && (
                <button onClick={() => setIsEditingSchoolName(false)} style={styles.secondaryButton}>
                  Cancel
                </button>
              )}
            </div>
          </div>
        )}
      </section>

      {/* Activities management section */}
      <section style={styles.activitiesManagementSection}>
        <h3 style={styles.sectionTitle}>Additional Activities</h3>
        <p style={styles.sectionDescription}>
          Create additional activities that students can complete to earn points for their house.
        </p>

        {/* Activities list */}
        {activityCollection.length > 0 && (
          <div style={styles.activitiesGrid}>
            {activityCollection.map(activity => (
              <article key={activity.id} style={styles.activityCard}>
                <div style={styles.activityContent}>
                  <div style={styles.activityName}>{activity.name}</div>
                  <div style={styles.activityPoints}>+{activity.points} points</div>
                </div>
                <button 
                  onClick={() => removeActivity(activity.id)} 
                  style={styles.deleteActivityButton}
                  aria-label={`Delete activity: ${activity.name}`}
                >
                  Delete
                </button>
              </article>
            ))}
          </div>
        )}

        {/* New activity creation section */}
        <div style={styles.newActivitySection}>
          <h4 style={styles.subsectionTitle}>Add New Activity</h4>
          
          <label>
            <div style={styles.fieldLabel}>Activity Name</div>
            <input 
              value={newActivityName} 
              onChange={(e) => setNewActivityName(e.target.value)} 
              style={styles.inputField}
              placeholder="e.g., Share on Social Media"
              aria-label="New activity name"
            />
          </label>
          
          <label>
            <div style={styles.fieldLabel}>Point Value</div>
            <input 
              type="number" 
              min="0"
              max="100"
              value={newActivityPoints} 
              onChange={(e) => setNewActivityPoints(Number(e.target.value))} 
              style={styles.inputField}
              aria-label="Point value for activity"
            />
          </label>
          
          <button onClick={createNewActivity} style={styles.secondaryButton}>
            Add Activity
          </button>
        </div>
      </section>

      {/* Word Scrambles management section */}
      <section style={styles.wordScramblesSection}>
        <h3 style={styles.sectionTitle}>Word Scrambles</h3>
        <p style={styles.sectionDescription}>
          Create word scramble challenges for students to solve and earn points.
        </p>

        {/* Word scrambles list */}
        {wordScrambleCollection.length > 0 && (
          <div style={styles.scramblesGrid}>
            {wordScrambleCollection.map(scramble => (
              <article key={scramble.id} style={styles.scrambleCard}>
                <div style={styles.scrambleContent}>
                  <div style={styles.scrambleRow}>
                    <span style={styles.scrambleLabel}>Scrambled:</span>
                    <span style={styles.scrambleWord}>{scramble.scrambledWord}</span>
                  </div>
                  <div style={styles.scrambleRow}>
                    <span style={styles.scrambleLabel}>Answer:</span>
                    <span style={styles.scrambleAnswer}>{scramble.unscrambledWord}</span>
                  </div>
                  <div style={styles.scramblePoints}>+{scramble.points} points</div>
                </div>
                <button 
                  onClick={() => removeWordScramble(scramble.id)} 
                  style={styles.deleteScrambleButton}
                  aria-label={`Delete word scramble: ${scramble.scrambledWord}`}
                >
                  Delete
                </button>
              </article>
            ))}
          </div>
        )}

        {/* New word scramble creation section */}
        <div style={styles.newScrambleSection}>
          <h4 style={styles.subsectionTitle}>Add New Word Scramble</h4>
          
          <label>
            <div style={styles.fieldLabel}>Scrambled Word (what students see)</div>
            <input 
              value={newScrambledWord} 
              onChange={(e) => setNewScrambledWord(e.target.value)} 
              style={styles.inputField}
              placeholder="e.g., RGFIFNY"
              aria-label="Scrambled word"
            />
          </label>
          
          <label>
            <div style={styles.fieldLabel}>Unscrambled Word (correct answer)</div>
            <input 
              value={newUnscrambledWord} 
              onChange={(e) => setNewUnscrambledWord(e.target.value)} 
              style={styles.inputField}
              placeholder="e.g., GRYFFINDOR"
              aria-label="Unscrambled word"
            />
          </label>
          
          <label>
            <div style={styles.fieldLabel}>Point Value</div>
            <input 
              type="number" 
              min="0"
              max="100"
              value={newScramblePoints} 
              onChange={(e) => setNewScramblePoints(Number(e.target.value))} 
              style={styles.inputField}
              aria-label="Point value for word scramble"
            />
          </label>
          
          <button onClick={createNewWordScramble} style={styles.secondaryButton}>
            Add Word Scramble
          </button>
        </div>
      </section>

      {/* Quiz collection management section */}
      <section style={styles.quizManagementSection}>
        <h3 style={styles.sectionTitle}>Quiz Management</h3>
        <div style={styles.quizGrid}>
          {quizCollection.map(quiz => (
            <article key={quiz.id} style={styles.quizCard}>
              <div style={styles.quizContent}>
                <div style={styles.quizTitle}>{quiz.title}</div>
                
                {/* Quiz questions preview */}
                <div style={styles.questionsSection}>
                  <strong style={styles.questionsHeader}>Questions:</strong>
                  <ul style={styles.questionsList}>
                    {quiz.questions.map((question, questionIndex) => (
                      <li key={questionIndex} style={styles.questionItem}>
                        <div style={styles.questionText}>
                          {question.text} ({question.points} pts)
                        </div>
                        <ul style={styles.optionsList}>
                          {question.options.map((option, optionIndex) => (
                            <li 
                              key={optionIndex} 
                              style={{
                                ...styles.optionItem,
                                color: optionIndex === question.correctIndex ? '#22c55e' : '#fff'
                              }}
                            >
                              {optionIndex + 1}. {option}
                            </li>
                          ))}
                        </ul>
                        <button 
                          onClick={() => removeQuestionFromQuiz(quiz.id, questionIndex)} 
                          style={styles.deleteQuestionButton}
                          aria-label={`Delete question: ${question.text}`}
                        >
                          Delete Question
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              {/* Quiz actions */}
              <div style={styles.quizActions}>
                <button
                  onClick={() => setActiveQuizId(quiz.id)}
                  style={{
                    ...styles.selectButton,
                    background: activeQuizId === quiz.id ? '#334155' : 'transparent',
                  }}
                  aria-label={`${activeQuizId === quiz.id ? 'Currently selected' : 'Select'} quiz: ${quiz.title}`}
                >
                  {activeQuizId === quiz.id ? 'Selected' : 'Select'}
                </button>
                <button 
                  onClick={() => removeQuiz(quiz.id)} 
                  style={styles.deleteQuizButton}
                  aria-label={`Delete quiz: ${quiz.title}`}
                >
                  Delete Quiz
                </button>
              </div>
            </article>
          ))}
        </div>

        {/* New quiz creation section */}
        <div style={styles.newQuizSection}>
          <h4 style={styles.subsectionTitle}>Add New Quiz</h4>
          <label>
            <div style={styles.fieldLabel}>Quiz Title</div>
            <input 
              value={newQuizTitle} 
              onChange={(e) => setNewQuizTitle(e.target.value)} 
              style={styles.inputField}
              placeholder="Enter quiz title"
              aria-label="New quiz title"
            />
          </label>
          <button onClick={createNewQuiz} style={styles.secondaryButton}>
            Add Quiz
          </button>
        </div>

        {/* Question creation section */}
        <div style={styles.newQuestionSection}>
          <h4 style={styles.subsectionTitle}>Add Question to Selected Quiz</h4>
          
          <label>
            <div style={styles.fieldLabel}>Question Text</div>
            <input 
              value={questionText} 
              onChange={(e) => setQuestionText(e.target.value)} 
              style={styles.inputField}
              placeholder="Enter the question text"
              aria-label="Question text"
            />
          </label>
          
          {/* Answer options */}
          {[0, 1, 2, 3].map(optionIndex => (
            <label key={optionIndex}>
              <div style={styles.fieldLabel}>Option {optionIndex + 1}</div>
              <input
                value={answerOptions[optionIndex]}
                onChange={(e) => {
                  const updatedOptions = [...answerOptions];
                  updatedOptions[optionIndex] = e.target.value;
                  setAnswerOptions(updatedOptions);
                }}
                style={styles.inputField}
                placeholder={`Enter option ${optionIndex + 1}`}
                aria-label={`Answer option ${optionIndex + 1}`}
              />
            </label>
          ))}
          
          <label>
            <div style={styles.fieldLabel}>Correct Answer</div>
            <select 
              value={correctAnswerIndex} 
              onChange={(e) => setCorrectAnswerIndex(Number(e.target.value))} 
              style={styles.selectField}
              aria-label="Select correct answer option"
            >
              {[0, 1, 2, 3].map(optionIndex => (
                <option key={optionIndex} value={optionIndex}>
                  Option {optionIndex + 1}
                </option>
              ))}
            </select>
          </label>
          
          <label>
            <div style={styles.fieldLabel}>Point Value</div>
            <input 
              type="number" 
              min="0"
              max="100"
              value={questionPointValue} 
              onChange={(e) => setQuestionPointValue(e.target.value)} 
              style={styles.inputField}
              aria-label="Point value for correct answer"
            />
          </label>
          
          <button onClick={addQuestionToActiveQuiz} style={styles.secondaryButton}>
            Add Question
          </button>
        </div>
      </section>
    </div>
  );
}

/**
 * Consolidated style definitions for AdminSchool component
 * Organizes all visual styling for maintainability and consistency
 */
const styles = {
  mainContainer: {
    display: 'grid',
    gap: 16,
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  
  adminHeader: {
    marginBottom: 12,
    fontStyle: 'italic',
    opacity: 0.8,
  },
  
  navigationButtons: {
    display: 'flex',
    gap: 8,
    marginTop: 8,
  },
  
  schoolNameSection: {
    marginTop: 16,
  },
  
  sectionTitle: {
    color: '#e2e8f0',
    marginBottom: 12,
  },
  
  subsectionTitle: {
    color: '#e2e8f0',
    fontSize: '1.1rem',
    marginBottom: 8,
  },
  
  schoolNameDisplay: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  
  schoolNameText: {
    fontFamily: "'Cinzel Decorative', serif",
    fontSize: 22,
    color: '#e2e8f0',
  },
  
  schoolNameEditor: {
    display: 'grid',
    gap: 12,
    width: '100%',
    maxWidth: '100%',
  },
  
  sectionDescription: {
    color: '#94a3b8',
    fontSize: '0.9rem',
    marginTop: -8,
    marginBottom: 12,
  },
  
  activitiesManagementSection: {
    marginTop: 24,
  },
  
  activitiesGrid: {
    display: 'grid',
    gap: 10,
    marginBottom: 20,
  },
  
  activityCard: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    border: '1px solid #334155',
    borderRadius: 10,
    padding: '12px 14px',
    background: '#0b1220',
  },
  
  activityContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  
  activityName: {
    fontWeight: 600,
    color: '#e2e8f0',
    fontSize: '0.95rem',
  },
  
  activityPoints: {
    color: '#eab308',
    fontSize: '0.85rem',
    fontWeight: 600,
  },
  
  deleteActivityButton: {
    padding: '6px 12px',
    borderRadius: 6,
    border: '1px solid #ef4444',
    background: 'transparent',
    color: '#f87171',
    cursor: 'pointer',
    fontSize: '0.85rem',
  },
  
  newActivitySection: {
    borderTop: '1px solid #334155',
    paddingTop: 16,
    display: 'grid',
    gap: 12,
  },
  
  wordScramblesSection: {
    marginTop: 24,
  },
  
  scramblesGrid: {
    display: 'grid',
    gap: 10,
    marginBottom: 20,
  },
  
  scrambleCard: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    border: '1px solid #334155',
    borderRadius: 10,
    padding: '12px 14px',
    background: '#0b1220',
  },
  
  scrambleContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  
  scrambleRow: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  
  scrambleLabel: {
    color: '#94a3b8',
    fontSize: '0.85rem',
    minWidth: '80px',
  },
  
  scrambleWord: {
    fontWeight: 600,
    color: '#e2e8f0',
    fontSize: '0.95rem',
    letterSpacing: '1px',
  },
  
  scrambleAnswer: {
    fontWeight: 600,
    color: '#22c55e',
    fontSize: '0.95rem',
  },
  
  scramblePoints: {
    color: '#eab308',
    fontSize: '0.85rem',
    fontWeight: 600,
    marginTop: 2,
  },
  
  deleteScrambleButton: {
    padding: '6px 12px',
    borderRadius: 6,
    border: '1px solid #ef4444',
    background: 'transparent',
    color: '#f87171',
    cursor: 'pointer',
    fontSize: '0.85rem',
  },
  
  newScrambleSection: {
    borderTop: '1px solid #334155',
    paddingTop: 16,
    display: 'grid',
    gap: 12,
  },
  
  quizManagementSection: {
    marginTop: 24,
  },
  
  quizGrid: {
    display: 'grid',
    gap: 12,
  },
  
  quizCard: {
    display: 'flex',
    gap: 12,
    alignItems: 'flex-start',
    border: '1px solid #334155',
    borderRadius: 12,
    padding: 12,
    background: '#0b1220',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  
  quizContent: {
    flex: 1,
    minWidth: 0, // Allows flex child to shrink below its content width
    wordBreak: 'break-word',
  },
  
  quizTitle: {
    fontWeight: 600,
    color: '#e2e8f0',
    marginBottom: 4,
    fontSize: '1.05rem',
  },
  
  quizId: {
    opacity: 0.7,
    color: '#94a3b8',
  },
  
  questionsSection: {
    marginTop: 8,
  },
  
  questionsHeader: {
    color: '#e2e8f0',
  },
  
  questionsList: {
    paddingLeft: 16,
    margin: '8px 0',
  },
  
  questionItem: {
    fontSize: 14,
    marginBottom: 8,
    listStyleType: 'disc',
  },
  
  questionText: {
    color: '#e2e8f0',
    marginBottom: 4,
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
  },
  
  optionsList: {
    paddingLeft: 16,
    margin: '4px 0',
  },
  
  optionItem: {
    fontSize: 13,
    listStyleType: 'none',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
  },
  
  quizActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    flexShrink: 0,
    minWidth: 'fit-content',
  },
  
  selectButton: {
    padding: '8px 10px',
    borderRadius: 8,
    border: '1px solid #475569',
    color: '#a0b4ff',
    cursor: 'pointer',
    minWidth: 80,
  },
  
  deleteQuizButton: {
    padding: '8px 10px',
    borderRadius: 8,
    border: '1px solid #ef4444',
    background: 'transparent',
    color: '#f87171',
    cursor: 'pointer',
  },
  
  deleteQuestionButton: {
    padding: '4px 8px',
    borderRadius: 6,
    border: '1px solid #ef4444',
    background: 'transparent',
    color: '#f87171',
    cursor: 'pointer',
    fontSize: 12,
    marginTop: 4,
  },
  
  newQuizSection: {
    borderTop: '1px solid #334155',
    marginTop: 24,
    paddingTop: 20,
  },
  
  newQuestionSection: {
    borderTop: '1px solid #334155',
    marginTop: 24,
    paddingTop: 20,
    display: 'grid',
    gap: 16,
  },
  
  fieldLabel: {
    color: '#e2e8f0',
    marginBottom: 6,
    fontSize: '0.95rem',
    fontWeight: 500,
  },
  
  inputField: {
    width: '100%',
    maxWidth: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #334155',
    background: '#0f172a',
    color: '#fff',
    outline: 'none',
    marginBottom: 12,
    boxSizing: 'border-box',
  },
  
  selectField: {
    width: '100%',
    maxWidth: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #334155',
    background: '#0f172a',
    color: '#fff',
    outline: 'none',
    marginBottom: 12,
    boxSizing: 'border-box',
  },
  
  buttonGroup: {
    display: 'flex',
    gap: 12,
    marginTop: 4,
  },
  
  primaryButton: {
    padding: '10px 16px',
    borderRadius: 8,
    border: '1px solid transparent',
    background: '#4f46e5',
    color: '#fff',
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: '0.95rem',
    whiteSpace: 'nowrap',
  },
  
  secondaryButton: {
    padding: '10px 16px',
    borderRadius: 8,
    border: '1px solid #475569',
    background: 'transparent',
    color: '#a0b4ff',
    cursor: 'pointer',
    fontSize: '0.95rem',
    whiteSpace: 'nowrap',
  },
};


